import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';

export default function PrivacyOps() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workingId, setWorkingId] = useState('');
  const [requests, setRequests] = useState([]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/admin/platform/privacy-requests');
      setRequests(response.data.requests || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load privacy requests');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function updateStatus(requestId, status) {
    const resolutionNote = window.prompt('Resolution note (optional):', '') ?? '';
    const fulfillmentUrl = status === 'fulfilled'
      ? (window.prompt('Fulfillment URL if available (optional):', '') ?? '')
      : '';
    setWorkingId(`${requestId}:${status}`);
    try {
      await api.post(`/admin/platform/privacy-requests/${requestId}/status`, {
        status,
        resolutionNote,
        fulfillmentUrl,
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not update this privacy request');
    } finally {
      setWorkingId('');
    }
  }

  return (
    <Layout>
      <div className="space-y-8">
        <section className="rounded-3xl border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top_left,rgba(245,178,33,0.16),transparent_34%),linear-gradient(180deg,#171717,#090909)] p-6">
          <p className="text-sm uppercase tracking-[0.2em] text-[#f7d88a]">Privacy Ops</p>
          <h2 className="mt-3 text-3xl font-semibold text-white">Data export, deletion, and trust requests</h2>
          <p className="mt-2 max-w-3xl text-sm text-gray-400">
            The app now sends privacy actions into a dedicated web queue, so exports and deletion requests stay visible and auditable.
          </p>
        </section>

        {error ? <p className="text-sm text-red-400">{error}</p> : null}

        <section className="rounded-3xl border border-gray-800 bg-gray-950 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-white">Privacy request queue</h3>
            <button
              type="button"
              onClick={load}
              className="rounded-full border border-[#f5b221]/40 px-4 py-2 text-sm font-semibold text-[#f7d88a] transition hover:bg-[#f5b221]/10"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <p className="mt-6 text-sm text-gray-400">Loading privacy queue...</p>
          ) : (
            <div className="mt-6 space-y-4">
              {requests.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-700 p-5 text-sm text-gray-400">
                  No privacy requests are waiting right now.
                </div>
              ) : (
                requests.map((item) => (
                  <div key={item.id} className="rounded-2xl border border-gray-800 bg-black/60 p-5">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <h4 className="text-lg font-semibold text-white">{item.user?.name || 'Unknown user'}</h4>
                        <p className="text-sm text-gray-400">
                          {item.type} · {item.user?.email || 'No email'} · {item.user?.shimbirrId || 'No Shimbirr ID'}
                        </p>
                      </div>
                      <span className="rounded-full bg-[#f5b221]/15 px-3 py-1 text-xs uppercase text-[#f7d88a]">
                        {item.status}
                      </span>
                    </div>

                    <div className="mt-4 grid gap-3 text-sm text-gray-400 md:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <p className="text-xs uppercase text-gray-500">Requested</p>
                        <p className="mt-1 text-white">{item.createdAt ? new Date(item.createdAt).toLocaleString() : 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-gray-500">Channel</p>
                        <p className="mt-1 text-white">{item.requestedFrom || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-gray-500">Assigned</p>
                        <p className="mt-1 text-white">{item.assignedAdmin?.name || 'Nobody yet'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-gray-500">Fulfillment</p>
                        <p className="mt-1 text-white">{item.fulfillmentUrl || 'Not attached'}</p>
                      </div>
                    </div>

                    {item.notes ? (
                      <p className="mt-4 rounded-2xl bg-gray-900 px-4 py-3 text-sm text-gray-300">{item.notes}</p>
                    ) : null}
                    {item.resolutionNote ? (
                      <p className="mt-4 rounded-2xl bg-gray-900 px-4 py-3 text-sm text-gray-300">Resolution: {item.resolutionNote}</p>
                    ) : null}

                    <div className="mt-4 flex flex-wrap gap-3">
                      {[
                        ['in_review', 'Mark in review'],
                        ['fulfilled', 'Mark fulfilled'],
                        ['rejected', 'Reject'],
                        ['cancelled', 'Cancel'],
                      ].map(([status, label]) => (
                        <button
                          key={status}
                          type="button"
                          onClick={() => updateStatus(item.id, status)}
                          disabled={workingId === `${item.id}:${status}`}
                          className="rounded-full border border-gray-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-900 disabled:opacity-60"
                        >
                          {workingId === `${item.id}:${status}` ? 'Working...' : label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
